import React, {useState} from 'react'
import {useMutation} from 'react-query'
import {Button, ServerSideAutocomplete, TextField} from 'finsys-webcomponent'
import {
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material'

import {useSnackBar} from 'hoc/SnackbarHandler'
import {fetchEmployees} from 'api/TS/query/_CommonQueries'
import {checkEmployeeExists} from 'api/TS/query/InternalRequestQueries'

export default function EmployeeFreeSoloCreateOptionDialogMultiple({
  disabled,
  data,
  setFormArrayValue,
  selectedUser,
  keyName,
  subKeyName,
  error,
}) {
  const {setSnackBar} = useSnackBar()
  const [open, toggleOpen] = useState(false)

  const filter = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) =>
      `${option.full_name} ${option.email ? `(${option.email})` : ''}`,
  })

  const handleClose = () => {
    setDialogValue({
      email: '',
      name: '',
    })

    toggleOpen(false)
  }

  const [dialogValue, setDialogValue] = React.useState({
    email: '',
    name: '',
  })

  const doesEmployeeExists = useMutation(
    (employeeList) => checkEmployeeExists(employeeList),
    {
      onSuccess: (result) => {
        if (result.status === 'Success') {
          if (dialogValue.email === selectedUser.email) {
            setSnackBar((prevState) => ({
              ...prevState,
              open: true,
              type: 'error',
              message: 'You cannot add the selected user',
            }))

            return
          }

          data[data.length] = {
            full_name: dialogValue.name.trim(),
            email: dialogValue.email,
            new: true,
          }
          setFormArrayValue(data)

          handleClose()
        } else {
          setSnackBar((prevState) => ({
            ...prevState,
            open: true,
            type: 'error',
            message: result.message,
          }))
        }
      },
      onError: (err) => {
        setSnackBar((prevState) => ({...prevState, open: true}))
      },
    }
  )

  const handleSubmit = (event) => {
    event.preventDefault()
    doesEmployeeExists.mutate([dialogValue.email])
  }

  return (
    <React.Fragment>
      <ServerSideAutocomplete
        freeSolo
        multiple
        clearOnBlur
        disabled={disabled}
        value={data}
        handleOnChange={(value) => {
          const currentValueIndex = value.length - 1

          if (value.length === 0 || currentValueIndex < 0) {
            setFormArrayValue(value)
          }

          if (
            typeof value[currentValueIndex] === 'string' &&
            value[currentValueIndex].trim() !== ''
          ) {
            // timeout to avoid instant validation of the dialog's form.
            setTimeout(() => {
              toggleOpen(true)
              setDialogValue(currentValueIndex, keyName, subKeyName, {
                email: null,
                name: value[currentValueIndex].full_name?.trim(),
              })
            })
          } else if (value[currentValueIndex] && value[currentValueIndex].inputValue) {
            toggleOpen(true)
            setDialogValue({
              email: null,
              name: value[currentValueIndex].full_name,
            })
          } else {
            if (
              (typeof value[currentValueIndex] === 'string' &&
                value[currentValueIndex]?.trim() === '') ||
              value[currentValueIndex]?.full_name.trim() === null
            ) {
              return
            }

            if (value[currentValueIndex]?.email === selectedUser.email) {
              setSnackBar((prevState) => ({
                ...prevState,
                open: true,
                type: 'error',
                message: 'You cannot add the selected user',
              }))

              return
            }

            value[currentValueIndex] = {
              full_name: value[currentValueIndex]?.full_name ?? null,
              email: value[currentValueIndex]?.email ?? null,
              new: false,
            }

            let uniq = {}
            let uniqueValues = value.filter(
              (obj) => !uniq[obj.email] && (uniq[obj.email] = true)
            )

            setFormArrayValue(uniqueValues)
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params)

          // Suggest the creation of a new value
          if (params.inputValue?.trim() !== '') {
            filtered.push({
              inputValue: `Add "${params.inputValue?.trim()}"`,
              full_name: params.inputValue?.trim(),
            })
          }

          return filtered
        }}
        fetchOptions={(value) => fetchEmployees(value?.trim())}
        getOptionLabel={(option) => {
          // e.g value selected with enter, right from the input
          if (typeof option === 'string') {
            return option
          }
          if (option.inputValue) {
            return option.inputValue
          }

          return option.full_name
            ? `${option.full_name} ${option.email ? `(${option.email})` : ''}`
            : ''
        }}
        label={null}
        textFieldProps={{
          error: !!error,
          sx: {
            '& label': {display: 'none'},
            '& .MuiInput-root': {mt: 0},
          },
          placeholder: 'Search by name or email',
        }}
      />

      <Dialog
        open={open}
        fullWidth={true}
        maxWidth={'sm'}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          Missing Employee? Please, add it!
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              autoFocus
              required={true}
              id="name"
              value={dialogValue.name}
              onChange={(event) =>
                setDialogValue({
                  ...dialogValue,
                  name: event.target.value?.replace(/\b\w/g, (l) => l.toUpperCase()),
                })
              }
              label="Name"
              type="text"
              helperText={'eg. John Lee'}
            />
            <TextField
              autoFocus
              required={true}
              id="email"
              value={dialogValue.email}
              onChange={(event) =>
                setDialogValue({...dialogValue, email: event.target.value?.trim()})
              }
              label="Email"
              type="text"
              helperText={'Please input the user email - eg. johnlee@domain.com'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={handleClose} type={'text'} />
          <Button label="Add" onClick={handleSubmit} type="submit" />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}
